import {Box, Checkbox, Chip, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {ElevationApp} from "../ElevationApp";
import {EventEmitter2} from "../util/EventEmitter2";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {hasDoorPrep, hasFramePrep, hasQty, isHinge} from "../scenes/hardware/hardwarePreps";
import {HardwareItem} from "../../../../../api/Hardware";
import {isDeadboltItem} from "../../hardware/prepCanvas/PrepManager";
import {getProductCode} from "../../hardware/HardwareGroup";
import {doorPlacementViolations, HardwarePlacementViolation} from "../model/hardware/MdHardwareRules";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {useEffect, useReducer} from "react";

const WIDTH = 340;
const STRIP_WIDTH = 28;

// the hardware workspace's right sidebar: the opening's hardware group with every item
// and its active / inactive / common qtys. Each placeable item has a checkbox that puts
// it on (or takes it off) the drawing. Folds to a thin strip; the collapsed state is
// remembered in viewSettings.hardwareSidebarCollapsed.
export function HardwareSidebar(props: {app: EventEmitter2<ElevationApp | null>}) {
    const app = useEventEmitter2(props.app);
    const workspace = useEventEmitter2(app?.viewSettings.workspace);
    const collapsed = useEventEmitter2(app?.viewSettings.hardwareSidebarCollapsed);
    const ready = useEventEmitter2(app?.ready);
    // re-render the checkbox state as items are added/removed.
    const listVersion = useEventEmitter2(app?.hardware.listChanged);

    // re-render as placements move (drag / dimension edit) so the rule warnings track them.
    const [, forceRender] = useReducer((x: number) => x + 1, 0);
    useEffect(() => {
        if(!app) return;
        const subs = app.hardware.list.flatMap(p => [p.x.subscribe(() => forceRender()), p.y.subscribe(() => forceRender())]);
        return () => subs.forEach(sub => sub.cancel());
    }, [app, listVersion]);

    if(!app || !ready || workspace !== "hardware") return null;

    const setCollapsed = (value: boolean) => app.viewSettings.hardwareSidebarCollapsed.emit(value);

    if(collapsed) {
        return (
            <Paper square elevation={0} sx={{
                width: STRIP_WIDTH,
                flexShrink: 0,
                borderLeft: 1,
                borderColor: "divider",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: 0.5,
            }}>
                <Tooltip title="Show hardware" placement="left">
                    <IconButton size="small" onClick={() => setCollapsed(false)}>
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" sx={{
                    writingMode: "vertical-rl",
                    mt: 1,
                    userSelect: "none",
                }}>
                    Hardware
                </Typography>
            </Paper>
        );
    }

    const group = app.hardwareGroup;
    const items = (group?.hardware ?? []).filter(i => !i.archived);
    const isPair = app.frameSpec.isPair;
    const deadbolt = items.find(i => isDeadboltItem(i, isPair)) ?? null;

    const toggle = (item: HardwareItem, checked: boolean) => {
        if(checked) app.hardware.add(item);
        else app.hardware.remove(item.product);
    };

    return (
        <Paper square elevation={0} sx={{
            width: WIDTH,
            flexShrink: 0,
            borderLeft: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
        }}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, borderBottom: 1, borderColor: "divider"}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography variant="subtitle2" noWrap>
                        {group ? `Hardware Group ${group.name}` : "Hardware"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                        Deadbolt: {deadbolt ? deadbolt.productCode : "None"}
                    </Typography>
                </Box>
                <Tooltip title="Hide hardware" placement="left">
                    <IconButton size="small" onClick={() => setCollapsed(true)}>
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{flex: 1, minHeight: 0, overflow: "auto"}}>
                {items.length === 0 && (
                    <Typography color="text.secondary" variant="body2" sx={{py: 2, textAlign: "center"}}>
                        {group ? "No hardware in this group." : "No hardware group on this opening."}
                    </Typography>
                )}
                {items.length > 0 && (
                    <Table size="small" stickyHeader sx={{
                        // fixed layout so the Item column shrinks to the sidebar width and
                        // its text ellipsizes instead of widening the table.
                        tableLayout: "fixed",
                        width: "100%",
                        "& td, & th": {px: 0.5, py: 0},
                        "& th": {fontSize: 11, lineHeight: 1.2, py: 0.25},
                        "& td.qty": {fontSize: 11, color: "text.secondary"},
                    }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{width: 28}} />
                                <TableCell>Item</TableCell>
                                <TableCell align="right" sx={{width: 26}}><Tooltip title="Active qty"><span>A</span></Tooltip></TableCell>
                                <TableCell align="right" sx={{width: 26}}><Tooltip title="Inactive qty"><span>I</span></Tooltip></TableCell>
                                <TableCell align="right" sx={{width: 26}}><Tooltip title="Common qty"><span>C</span></Tooltip></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(item => (
                                <HardwareRow
                                    key={item.id}
                                    item={item}
                                    isPair={isPair}
                                    checked={app.hardware.has(item.product)}
                                    violations={app.hardware.list
                                        .filter(p => p.productId === item.product)
                                        .flatMap(p => doorPlacementViolations(app, p, item, app.draw.formatDimension))}
                                    onToggle={checked => toggle(item, checked)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Paper>
    );
}

function HardwareRow(props: {item: HardwareItem; isPair: boolean; checked: boolean; violations: HardwarePlacementViolation[]; onToggle: (checked: boolean) => void}) {
    const item = props.item;
    const hinge = isHinge(item);
    const door = hasDoorPrep(item);
    const frame = hasFramePrep(item);
    const placeable = !hinge && (door || frame);

    // for a pair, note which leaf(s) the door prep lands on. A single opening only has
    // the active leaf, so the label would be noise.
    const hasActive = door && hasQty(item, true);
    const hasInactive = door && hasQty(item, false);
    const leaf = door && props.isPair
        ? (hasActive && hasInactive ? "Both leaves" : hasActive ? "Active leaf" : hasInactive ? "Inactive leaf" : null)
        : null;

    // hinges are always on the drawing (placed by the Hinges tools), so their box is
    // checked and locked.
    const prep = hinge ? "Hinges are always shown; edit them with the Hinges tools"
        : door && frame ? "Door + Frame"
        : door ? "Door"
        : frame ? "Frame"
        : "No prep";
    const hint = [prep, leaf].filter(Boolean).join(" · ");
    const sku = getProductCode(item);

    return (
        <TableRow hover title={hint} sx={{cursor: placeable ? "pointer" : "default"}} onClick={() => placeable && props.onToggle(!props.checked)}>
            <TableCell>
                <Checkbox
                    size="small"
                    checked={hinge || props.checked}
                    disabled={!placeable}
                    onClick={e => e.stopPropagation()}
                    onChange={e => props.onToggle(e.target.checked)}
                    sx={{p: 0, display: "block"}}
                />
            </TableCell>
            <TableCell sx={{overflow: "hidden"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5, minWidth: 0}}>
                    <Typography variant="body2" noWrap sx={{flex: 1, minWidth: 0}}>{item.category}</Typography>
                    {props.violations.length > 0 && (
                        <Tooltip title={<>{props.violations.map((v, i) => <div key={i}>{v.category}: {v.message}</div>)}</>}>
                            <WarningAmberIcon color="error" sx={{fontSize: 16}} />
                        </Tooltip>
                    )}
                    {isDeadboltItem(item, props.isPair) && <Chip size="small" color="secondary" variant="outlined" label="Deadbolt" sx={{height: 18, fontSize: 10}} />}
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap component="div">{sku}</Typography>
            </TableCell>
            {item.productIsAutoQtyHinge ? (
                <>
                    <TableCell className="qty" align="right"><AutoQty override={item.overrideHingeActiveQty} /></TableCell>
                    <TableCell className="qty" align="right"><AutoQty override={item.overrideHingeInActiveQty} /></TableCell>
                    <TableCell className="qty" align="right">-</TableCell>
                </>
            ) : (
                <>
                    <TableCell className="qty" align="right">{item.activeQty}</TableCell>
                    <TableCell className="qty" align="right">{item.inactiveQty}</TableCell>
                    <TableCell className="qty" align="right">{item.commonQty}</TableCell>
                </>
            )}
        </TableRow>
    );
}

// an auto-qty hinge's leaf qty: "*" for auto (by door height, as the hardware group editor
// marks it), or the override when one is set.
function AutoQty(props: {override: number | null}) {
    if(props.override !== null && props.override !== undefined) {
        return <Tooltip title={`Auto calculated, overridden to ${props.override}`}><span>{props.override}</span></Tooltip>;
    }
    return <Tooltip title="Auto calculated"><span>*</span></Tooltip>;
}
